/**
 * Manpower P&L — Daily Attendance: the SINGLE entry point for marking manpower attendance.
 *
 * Flow: pick Facility + Date + Day/Night → "Open roll" (POST /attendance/roll) → the shift is
 * auto-created if needed and the pre-filled roster loads inline → mark present/absent + OT →
 * Save (bulk-mark) → Freeze (runs the cost engine). There is NO user-facing shift id and no
 * separate shift-roll screen — the internal shift_id from the roll result is used for
 * save/freeze under the hood.
 *
 * The roster is AUTO-BUILT from each worker's facility assignment; you only mark it.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { Toggle } from '../../../components/ui';
import { useIsMobile } from '../../../pages/email-workflows/WorkflowTable';
import { useCities } from '../../../features/manpower-vendors/hooks/useCities';
import { useVendorData } from '../../../features/manpower-vendors/hooks/useVendorData';
import { useFacilities } from '../../../features/manpower-workers/hooks/useFacilities';
import { ManpowerAttendanceApiService } from '../../../services/manpowerAttendanceApi';
import type { AttendanceRow, OpenRollResult } from '../../../services/manpowerAttendanceApi';
import { ManpowerWorkersApiService, Worker } from '../../../services/manpowerWorkersApi';

type ShiftType = 'Day' | 'Night';
// the generated Worker type has id?: — the picker only offers rows that HAVE one
type AdhocWorker = Worker & { id: number };
interface Draft {
	status: 'present' | 'absent';
	otHours: number;
}

/** Today's date as YYYY-MM-DD in local time. */
const todayIso = (): string => {
	const now = new Date();
	const off = now.getTimezoneOffset();
	return new Date(now.getTime() - off * 60 * 1000).toISOString().slice(0, 10);
};

export const AttendanceDaily: React.FC = () => {
	// City scoping: a user with a city_id is a City Head — lock them to their city.
	// Leadership (city_id null/undefined, e.g. founders) keeps the free "All cities" picker.
	const user = useSelector((state: RootState) => state.auth.user);
	const scopedCityId = user?.city_id ?? null;
	const isCityScoped = scopedCityId != null;

	const [cityId, setCityId] = useState<number | ''>(scopedCityId ?? '');
	const [facilityId, setFacilityId] = useState<number | ''>('');
	const [date, setDate] = useState<string>(todayIso());
	const [shiftType, setShiftType] = useState<ShiftType>('Day');
	const [roll, setRoll] = useState<OpenRollResult | null>(null);
	const [drafts, setDrafts] = useState<Record<number, Draft>>({});
	const [toast, setToast] = useState<string | null>(null);
	const [confirmFreeze, setConfirmFreeze] = useState(false);
	// ad-hoc workers pulled onto THIS shift (server: bulkMark isDynamic=true —
	// curl-verified 2026-07-06, the row comes back with is_dynamic=1)
	const [adhoc, setAdhoc] = useState<AdhocWorker[]>([]);
	const [adhocOpen, setAdhocOpen] = useState(false);
	const [adhocOptions, setAdhocOptions] = useState<AdhocWorker[] | null>(null);
	// phone: once the roll is open the setup collapses to a summary line; "Change" reopens it
	const [changingSetup, setChangingSetup] = useState(false);

	const { data: cities = [] } = useCities();
	const { data: facilities = [] } = useFacilities(cityId === '' ? undefined : cityId);
	const { data: vendorsResp } = useVendorData();
	const vendorNameById = useMemo(() => {
		const m = new Map<number, string>();
		(vendorsResp?.data ?? []).forEach(v => { if (v.id != null) m.set(v.id, v.name ?? ''); });
		return m;
	}, [vendorsResp]);

	// For a city-scoped user, auto-select their single facility once the list loads
	// (so they land ready to just hit "Open roll"). Leadership picks manually.
	useEffect(() => {
		if (isCityScoped && facilityId === '' && facilities.length > 0) {
			setFacilityId(facilities[0].id);
		}
	}, [isCityScoped, facilityId, facilities]);

	const showToast = (msg: string) => {
		setToast(msg);
		window.setTimeout(() => setToast(null), 3500);
	};

	const open = useMutation({
		mutationFn: () =>
			ManpowerAttendanceApiService.openRoll({
				facilityId: facilityId as number,
				date,
				shiftType,
			}),
		onSuccess: result => {
			setRoll(result);
			showToast(result.created ? 'New shift opened' : 'Existing shift loaded');
		},
		onError: e => showToast(e instanceof Error ? e.message : 'Failed to open roll'),
	});

	const shiftId = roll?.shift_id;
	const rows: AttendanceRow[] = useMemo(() => roll?.rows ?? [], [roll]);
	const frozen = rows.some(r => r.freeze_status === 'frozen');

	// Seed editable drafts from the loaded roster.
	// Default everyone PRESENT (toggle ON) for any EDITABLE shift — the city-head flips OFF
	// only the absentees. A FROZEN shift is finalized and read-only, so we show its real,
	// immutable statuses (you can't re-present a frozen-absent worker).
	useEffect(() => {
		if (!roll) return;
		const isFrozen = rows.some(r => r.freeze_status === 'frozen');
		const seed: Record<number, Draft> = {};
		for (const r of rows) {
			if (r.worker_id != null) {
				const status: Draft['status'] = isFrozen ? (r.status === 'present' ? 'present' : 'absent') : 'present';
				seed[r.worker_id] = { status, otHours: r.ot_hours ?? 0 };
			}
		}
		setDrafts(seed);
	}, [roll, rows]);

	const setDraft = (workerId: number, patch: Partial<Draft>) =>
		setDrafts(prev => ({ ...prev, [workerId]: { ...prev[workerId], ...patch } }));

	const presentCount = Object.values(drafts).filter(d => d.status === 'present').length;

	const save = useMutation({
		mutationFn: () => {
			const adhocIds = new Set(adhoc.map(w => w.id));
			const entries = Object.entries(drafts).map(([wid, d]) => ({
				workerId: Number(wid),
				status: d.status,
				otHours: d.otHours || 0,
				isDynamic: adhocIds.has(Number(wid)),
			}));
			return ManpowerAttendanceApiService.bulkMark(shiftId as number, entries);
		},
		onSuccess: () => showToast('Attendance saved'),
		onError: e => showToast(e instanceof Error ? e.message : 'Save failed'),
	});

	// roster changes reset the local ad-hoc list (new roll = clean slate)
	useEffect(() => {
		setAdhoc([]);
		setAdhocOpen(false);
	}, [roll]);

	const rosterIds = useMemo(() => new Set(rows.map(r => r.worker_id)), [rows]);
	const openAdhocPicker = async () => {
		setAdhocOpen(true);
		if (adhocOptions == null) {
			try {
				const res = await ManpowerWorkersApiService.listWorkers();
				setAdhocOptions(((res.data ?? []).filter(w => w.id != null)) as AdhocWorker[]);
			} catch {
				setAdhocOptions([]);
				showToast('Could not load the workers list');
			}
		}
	};
	const addAdhoc = (w: AdhocWorker) => {
		setAdhoc(prev => [...prev, w]);
		setDrafts(prev => ({ ...prev, [w.id]: { status: 'present', otHours: 0 } }));
		setAdhocOpen(false);
	};

	const freeze = useMutation({
		mutationFn: () => ManpowerAttendanceApiService.freeze(shiftId as number),
		onSuccess: res => {
			showToast(`Frozen — ${res.shiftCostRows ?? 0} cost rows, ₹${(res.totalCost ?? 0).toLocaleString('en-IN')}`);
			setConfirmFreeze(false);
			// reload the roll so freeze_status reflects
			open.mutate();
		},
		onError: e => {
			showToast(e instanceof Error ? e.message : 'Freeze failed');
			setConfirmFreeze(false);
		},
	});

	const canOpen = facilityId !== '';
	const isMobile = useIsMobile();
	const inputCls =
		'rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-green-500 focus:outline-none';
	// phone summary line: "Bengaluru ECity · Day · 6 Jul"
	const facilityLabel = facilities.find(f => f.id === facilityId)?.location ?? 'Facility';
	const prettyDate = (() => {
		const d = new Date(`${date}T00:00:00`);
		return Number.isNaN(d.getTime())
			? date
			: d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
	})();
	// desktop always shows the full form; phone collapses it once the roll is open
	const showSetupForm = !isMobile || !roll || changingSetup;

	return (
		<div className={`min-h-screen bg-white ${isMobile ? 'p-3' : 'p-6'} ${isMobile && roll ? 'pb-24' : ''}`}>
			<div className='max-w-7xl mx-auto'>
				<div className='mb-6'>
					<h1 className='text-xl font-semibold text-gray-900'>Daily Attendance</h1>
					<p className='text-sm text-gray-500'>
						Manpower P&amp;L — pick a facility, date and shift, open the roster, mark attendance, then freeze to
						compute cost.
					</p>
				</div>

				{/* Facility + date + shift selectors — full-width stack on phone, same DOM
				    order (specs rely on select nth(0)=City, nth(1)=Facility, one date input).
				    Phone: collapses to a summary line once the roll is open (tap Change to re-pick). */}
				{showSetupForm ? (
				<div className='mb-4 flex flex-wrap items-end gap-3'>
					<div className='flex w-full flex-col gap-1 sm:w-auto'>
						<label className='text-xs font-medium text-gray-700'>City</label>
						<select
							value={cityId}
							disabled={isCityScoped}
							onChange={e => {
								setCityId(e.target.value === '' ? '' : Number(e.target.value));
								setFacilityId('');
								setRoll(null);
							}}
							className={`${inputCls} w-full sm:w-44 disabled:bg-gray-100 disabled:text-gray-600`}
						>
							{/* Leadership only: the "all cities" choice. City-scoped users are locked. */}
							{!isCityScoped && <option value=''>All cities</option>}
							{cities.map(c => (
								<option key={c.id} value={c.id}>{c.name}</option>
							))}
						</select>
					</div>
					<div className='flex w-full flex-col gap-1 sm:w-auto'>
						<label className='text-xs font-medium text-gray-700'>Facility</label>
						<select
							value={facilityId}
							onChange={e => {
								setFacilityId(e.target.value === '' ? '' : Number(e.target.value));
								setRoll(null);
							}}
							className={`${inputCls} w-full sm:w-64`}
						>
							<option value=''>Select a facility…</option>
							{facilities.map(f => (
								<option key={f.id} value={f.id}>{f.location}</option>
							))}
						</select>
					</div>
					<div className='flex w-full flex-col gap-1 sm:w-auto'>
						<label className='text-xs font-medium text-gray-700'>Date</label>
						<input type='date' value={date} onChange={e => { setDate(e.target.value); setRoll(null); }} className={`${inputCls} w-full sm:w-auto`} />
					</div>
					<div className='flex w-full flex-col gap-1 sm:w-auto'>
						<label className='text-xs font-medium text-gray-700'>Shift</label>
						<select value={shiftType} onChange={e => { setShiftType(e.target.value as ShiftType); setRoll(null); }} className={`${inputCls} w-full sm:w-auto`}>
							<option value='Day'>Day</option>
							<option value='Night'>Night</option>
						</select>
					</div>
					<button
						type='button'
						onClick={() => { open.mutate(); setChangingSetup(false); }}
						disabled={!canOpen || open.isPending}
						className='w-full rounded-md bg-gray-800 px-4 py-2 text-sm font-medium text-white hover:bg-gray-900 disabled:opacity-50 sm:w-auto'
					>
						{open.isPending ? 'Opening…' : 'Open roll'}
					</button>
				</div>
				) : (
					<button
						type='button'
						onClick={() => setChangingSetup(true)}
						className='mb-3 flex w-full items-center justify-between rounded-md border border-gray-200 px-3 py-2.5 text-sm active:bg-gray-50'
					>
						<span className='font-medium text-gray-900'>{facilityLabel} · {shiftType} · {prettyDate}</span>
						<span className='font-medium text-primary'>Change</span>
					</button>
				)}


				{/* Roster meta + actions (actions move to the sticky bottom bar on phone) */}
				{roll && (
					<div className='mb-3 flex items-center justify-between rounded-md bg-gray-50 px-4 py-2 text-xs text-gray-700'>
						<span>
							{roll.shift_type} shift · {roll.date} · {presentCount}/{rows.length} present
							{frozen && <span className='ml-2 rounded-full bg-blue-50 px-2 py-0.5 text-blue-700'>frozen</span>}
						</span>
						{!isMobile && (
							<div className='flex gap-2'>
								<button
									type='button'
									onClick={() => save.mutate()}
									disabled={save.isPending || frozen || rows.length === 0}
									className='rounded-md bg-green-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-green-700 disabled:opacity-50'
								>
									{save.isPending ? 'Saving…' : 'Save attendance'}
								</button>
								<button
									type='button'
									onClick={() => setConfirmFreeze(true)}
									disabled={freeze.isPending || frozen || rows.length === 0}
									className='rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50'
								>
									Freeze shift
								</button>
							</div>
						)}
					</div>
				)}

				{/* Roster — phone: ONE dense line per worker (owner 2026-07-06: "simple
			    toggle against the name"), + add ad-hoc; desktop: the table. JS switch. */}
				{isMobile ? (
					<div className='-mx-2.5 bg-white'>
						{!roll ? (
							<div className='p-6 text-center text-sm text-gray-500'>Pick a facility, date and shift, then open the roll.</div>
						) : open.isPending ? (
							<div className='p-6 text-center text-sm text-gray-500'>Loading roster…</div>
						) : rows.length === 0 && adhoc.length === 0 ? (
							<div className='p-6 text-center text-sm text-gray-500'>No workers on this facility&apos;s roster.</div>
						) : (
							<div className='divide-y divide-gray-100'>
								<div className='flex items-center gap-2 px-3 py-1.5 text-[10px] font-medium uppercase tracking-wide text-gray-400'>
									<span className='w-6 shrink-0'>#</span>
										<span className='min-w-0 flex-1'>Worker</span>
									<span className='w-14 shrink-0 text-center'>OT h</span>
									<span className='w-11 shrink-0 text-center'>Present</span>
								</div>
								{rows.map((r, i) => {
									const wid = r.worker_id as number;
									const d = drafts[wid] ?? { status: 'absent', otHours: 0 };
									return (
										<div key={wid} data-testid='roster-card' className='flex items-center gap-2 px-3 py-2'>
											<span className='w-6 shrink-0 text-[11px] tabular-nums text-gray-400'>{i + 1}</span>
											<div className='min-w-0 flex-1'>
												<div className='truncate text-sm font-medium text-gray-900'>{r.name ?? `#${wid}`}</div>
												<div className='truncate text-xs text-gray-500'>{vendorNameById.get(r.vendor_id as number) || '—'}</div>
											</div>
											<input
												type='number'
												min={0}
												value={d.otHours}
												disabled={frozen || d.status !== 'present'}
												onChange={e => setDraft(wid, { otHours: Number(e.target.value) || 0 })}
												aria-label={`OT hours: ${r.name ?? `worker ${wid}`}`}
												title='OT hours'
												className='w-14 shrink-0 rounded-md border border-gray-300 px-1.5 py-1.5 text-sm text-right focus:border-green-500 focus:outline-none disabled:bg-gray-50 disabled:text-gray-300'
											/>
											<Toggle
												checked={d.status === 'present'}
												disabled={frozen}
												aria-label={`Present: ${r.name ?? `worker ${wid}`}`}
												onChange={on => setDraft(wid, { status: on ? 'present' : 'absent' })}
											/>
										</div>
									);
								})}
								{adhoc.map((w, i) => {
									const d = drafts[w.id] ?? { status: 'present', otHours: 0 };
									return (
										<div key={`adhoc-${w.id}`} data-testid='roster-card' className='flex items-center gap-2 bg-amber-50/60 px-3 py-2'>
											<span className='w-6 shrink-0 text-[11px] tabular-nums text-gray-400'>{rows.length + i + 1}</span>
											<div className='min-w-0 flex-1 truncate text-sm font-medium text-gray-900'>
												{w.name}
												<span className='ml-1 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800'>ad-hoc</span>
											</div>
											<input
												type='number'
												min={0}
												value={d.otHours}
												disabled={frozen || d.status !== 'present'}
												onChange={e => setDraft(w.id, { otHours: Number(e.target.value) || 0 })}
												aria-label={`OT hours: ${w.name}`}
												title='OT hours'
												className='w-14 shrink-0 rounded-md border border-gray-300 px-1.5 py-1.5 text-sm text-right focus:border-green-500 focus:outline-none disabled:bg-gray-50 disabled:text-gray-300'
											/>
											<Toggle
												checked={d.status === 'present'}
												disabled={frozen}
												aria-label={`Present: ${w.name}`}
												onChange={on => setDraft(w.id, { status: on ? 'present' : 'absent' })}
											/>
										</div>
									);
								})}
								{!frozen && (
									<div className='px-3 py-2'>
										{!adhocOpen ? (
											<button type='button' onClick={openAdhocPicker} className='w-full rounded-md py-2 text-sm font-semibold text-primary active:bg-gray-50'>
												+ Add ad-hoc worker
											</button>
										) : adhocOptions == null ? (
											<p className='py-2 text-center text-sm text-gray-500'>Loading workers…</p>
										) : (
											<select
												aria-label='Pick an ad-hoc worker'
												className='w-full rounded-md border border-gray-300 px-3 py-2.5 text-base focus:border-green-500 focus:outline-none'
												defaultValue=''
												onChange={e => {
													const w = adhocOptions.find(x => String(x.id) === e.target.value);
													if (w) addAdhoc(w);
												}}
											>
												<option value='' disabled>
													Pick a worker to add for this shift…
												</option>
												{adhocOptions
													.filter(w => !rosterIds.has(w.id) && !adhoc.some(a => a.id === w.id))
													.map(w => (
														<option key={w.id} value={w.id}>
															{w.name}
														</option>
													))}
											</select>
										)}
									</div>
								)}
							</div>
						)}
					</div>
				) : (
				<div className='bg-white rounded-lg border border-gray-200 overflow-hidden'>
					<div className='overflow-x-auto'>
						<table className='w-full border-collapse'>
							<thead>
								<tr className='bg-gray-50 border-b border-gray-200'>
									<th className='px-4 py-3 text-left text-xs font-bold text-gray-900'>Worker</th>
									<th className='px-4 py-3 text-left text-xs font-bold text-gray-900'>Vendor</th>
									<th className='px-4 py-3 text-center text-xs font-bold text-gray-900'>Present</th>
									<th className='px-4 py-3 text-right text-xs font-bold text-gray-900'>OT hrs</th>
								</tr>
							</thead>
							<tbody>
								{!roll ? (
									<tr><td colSpan={4} className='px-4 py-8 text-center text-gray-500 text-sm'>Pick a facility, date and shift, then open the roll.</td></tr>
								) : open.isPending ? (
									<tr><td colSpan={4} className='px-4 py-8 text-center text-gray-500 text-sm'>Loading roster…</td></tr>
								) : rows.length === 0 ? (
									<tr><td colSpan={4} className='px-4 py-8 text-center text-gray-500 text-sm'>No workers on this facility&apos;s roster.</td></tr>
								) : (
									rows.map(r => {
										const wid = r.worker_id as number;
										const d = drafts[wid] ?? { status: 'absent', otHours: 0 };
										return (
											<tr key={wid} className='border-b border-gray-200 hover:bg-gray-50'>
												<td className='px-4 py-2 text-xs text-gray-900'>{r.name ?? `#${wid}`}</td>
												<td className='px-4 py-2 text-xs text-gray-900'>{r.vendor_id ?? '—'}</td>
												<td className='px-4 py-2'>
													<div className='flex items-center justify-center'>
														<Toggle
															checked={d.status === 'present'}
															disabled={frozen}
															aria-label={`Present: ${r.name ?? `worker ${wid}`}`}
															onChange={on => setDraft(wid, { status: on ? 'present' : 'absent' })}
														/>
													</div>
												</td>
												<td className='px-4 py-2 text-right'>
													<input
														type='number'
														min={0}
														value={d.otHours}
														disabled={frozen || d.status !== 'present'}
														onChange={e => setDraft(wid, { otHours: Number(e.target.value) || 0 })}
														className='w-20 rounded-md border border-gray-300 px-2 py-1 text-xs text-right focus:border-green-500 focus:outline-none disabled:bg-gray-100'
													/>
												</td>
											</tr>
										);
									})
								)}
							</tbody>
						</table>
					</div>
				</div>
				)}
			</div>

			{/* phone: Save/Freeze ride a fixed bottom bar — thumbs live there */}
			{isMobile && roll && (
				<div className='fixed inset-x-0 bottom-0 z-40 flex gap-2 border-t border-gray-200 bg-white p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]'>
					<button
						type='button'
						onClick={() => save.mutate()}
						disabled={save.isPending || frozen || rows.length === 0}
						className='flex-1 rounded-md bg-green-600 py-3 text-sm font-semibold text-white disabled:opacity-50'
					>
						{save.isPending ? 'Saving…' : 'Save attendance'}
					</button>
					<button
						type='button'
						onClick={() => setConfirmFreeze(true)}
						disabled={freeze.isPending || frozen || rows.length === 0}
						className='flex-1 rounded-md bg-blue-600 py-3 text-sm font-semibold text-white disabled:opacity-50'
					>
						Freeze shift
					</button>
				</div>
			)}

			{/* Freeze confirm */}
			{confirmFreeze && (
				<div className='fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4'>
					<div className='w-full max-w-sm rounded-lg bg-white p-5 shadow-xl'>
						<h3 className='text-base font-semibold text-gray-900'>Freeze this shift?</h3>
						<p className='mt-2 text-sm text-gray-600'>
							Freezing runs the cost engine and writes cost rows to the ledger. This cannot be undone — save
							your marks first.
						</p>
						<div className='mt-5 flex justify-end gap-2'>
							<button type='button' onClick={() => setConfirmFreeze(false)} className='rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50'>Cancel</button>
							<button type='button' onClick={() => freeze.mutate()} disabled={freeze.isPending} className='rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50'>
								{freeze.isPending ? 'Freezing…' : 'Freeze'}
							</button>
						</div>
					</div>
				</div>
			)}

		{toast && (
				<div className='fixed bottom-20 sm:bottom-6 right-6 z-50 rounded-md bg-gray-900 px-4 py-2 text-sm text-white shadow-lg'>{toast}</div>
			)}
		</div>
	);
};

export default AttendanceDaily;
